import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { withRouter } from 'react-router-dom';
import Routes from '../../routes';
import {
    CommonModal,
    BreadCrumbs,
    LoadingBar,
    PrimaryButton,
    SecondaryButton,
    Stepper,
    PageTitleWithToolTip,
    ExitConfirmationModal
} from '../Common';
import LocationDetails from './LocationDetails';
import LocationAddress from './LocationAddress';
import LocationContact from './LocationContact'
import { GetLocationCodes } from "../../CodesCache";
import { Grey2 } from '../../color_utils';
import { addLocationCountries, filterCountries, stateCode, trakrefFontFamily } from '../../utils';
import {
    Grid,
    Typography,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';
import useNavigate from '../../hooks/useNavigate';

const AddLocation = ({
    history,
    isEditing = false,
    match,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [codes, setCodes] = useState([]);
    const [data, setData] = useState({});
    const [id, setID] = useState(null);
    const [successfulPopup, setSuccessfulPopup] = useState(false);
    const [exitModal, setExitModal] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const firstRef = useRef();
    const secondRef = useRef();
    const thirdRef = useRef();
    const [refs] = useState([firstRef, secondRef, thirdRef]);
    const navigate = useNavigate({
        pageName: 'AddLocation'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'AddLocation', text: isEditing ? 'Edit' : 'Create Location', link: history.location.pathname, shouldBeReplaced: !isEditing }
    })
    

    const onHandleSwitchStep = (step) => {
        const isValid = refs[activeStep].current.validate();

        if (isValid) {
            setActiveStep(step);
        }
    };

    const submit = async () => {
        setSaving(true);

        if (refs[2].current.validate() === false) {
            setSaving(false);
            return;
        }
        var first = firstRef.current.submit();
        var second = secondRef.current.submit();
        var third = thirdRef.current.submit();

        var combined = Object.assign(first, second);
        Object.assign(combined, third);

        combined.id = match.params.id;

        const response = await fetch(Routes.Location.AddLocation(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(combined)
        })

        const newdata = await response.json();

        if (response.status === 200) {
            setData(newdata);

            if (match.params.id) {
                toast.success("The location has been successfully edited.", { duration: 6000 });
                navigate.goBack(`/Location/${data.id}`);
            } else {
                setSuccessfulPopup(true);
            }
        } else {
            toast.error('There was an error saving the location', { duration: 6000 });
            if (newdata.length > 0) {
                newdata.forEach(i => toast.error(i.exceptionMessage, { duration: 6000 }));
            }
        }

        setSaving(false);
    };

    const onSubmit = async () => {
        if (activeStep === 2) {
            await submit();
            return;
        }

        onHandleSwitchStep(activeStep + 1);
    };

    const determineButtonText = () => {
        if (activeStep === 2) {
            return isEditing ? "Save" : "Create Location";
        }
        return "Next";
    };

    const handleCloseButton = () => {
        if (!exitModal) {
            return setExitModal(true);
        }

        navigate.goBack((isEditing ? `/Location/${data.id}` : "/Locations"));
    };

    const closeExitModal = () => setExitModal(false);

    useEffect(() => {
        const fetchData = async () => {
            if (match.params.id) {
                setID(match.params.id)
                const response = await fetch(Routes.Location.DetailsForEdit(match.params.id));
                const json = await response.json();
                setData(mapCountryAndStateCode(json));
            }

            const data = await GetLocationCodes();
            setCodes(data);

            setLoading(false);
        };

        fetchData();
    }, [match.params.id]);

    const findCountryOrStateCode = (object, value) => {
        const data = object.find(item => item.value === value);
        return data ? data.code : null;
    };

    const mapCountryAndStateCode = (data) => {
        data.physicalCountry = findCountryOrStateCode(addLocationCountries, data.physicalCountry) || data.physicalCountry;
        data.mailingCountry = findCountryOrStateCode(addLocationCountries, data.mailingCountry) || data.mailingCountry;
        if (filterCountries.includes(data.physicalCountry)) {
            data.physicalState = findCountryOrStateCode(stateCode, data.physicalState) || data.physicalState;
        }
        if (filterCountries.includes(data.mailingCountry)) {
            data.mailingState = findCountryOrStateCode(stateCode, data.mailingState) || data.mailingState;
        }
        return data;
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />

            <Grid
                container
                direction="row"
                alignItems="center"
                sx={{ maxWidth: '65%' }}
                ml={3}
            >
                <Grid item>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseButton}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle={isEditing ? "Edit Location" : "Create Location"}
                    />
                </Grid>
                <Grid item sx={{ marginLeft: 'auto' }}>
                    <PrimaryButton
                        buttonText={determineButtonText()}
                        onClick={onSubmit}
                        disabled={saving}
                    />
                </Grid>
            </Grid >

            <Grid container mb={4} ml={3} sx={{ maxWidth: "65%" }} spacing={2}>
                <Grid item xs={12} sx={{ marginTop: "7px" }}>
                    <Stepper
                        activeStep={activeStep}
                        steps={[
                            'Location Details', 'Address', 'Contact'
                        ]}
                        onStepChange={onHandleSwitchStep}
                        sx={{
                            gap: "15px",
                            marginBottom: "25px"
                        }}
                    />
                    <div style={{ display: activeStep === 0 ? "block" : "none" }}>
                        <LocationDetails ref={firstRef} setActiveStep={setActiveStep} codes={codes} data={data} id={id} />
                    </div>
                    <div style={{ display: activeStep === 1 ? "block" : "none" }}>
                        <LocationAddress ref={secondRef} setActiveStep={setActiveStep} data={data} />
                    </div>
                    <div style={{ display: activeStep === 2 ? "block" : "none" }}>
                        <LocationContact ref={thirdRef} setActiveStep={setActiveStep} data={data} />
                    </div>
                </Grid>
            </Grid>

            <CommonModal
                open={successfulPopup}
                close={() => {
                    setSuccessfulPopup(false);
                    navigate.goBack(`/Location/${data.id}`);
                }}
            >
                <Grid container>
                    <Grid item xs={12} align='center'>
                        <img src={'/images/LocationCreated.png'} width="311" height="180" alt="Success! :)" />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <Typography
                            variant="h5"
                            
                            style={{ marginBottom: '6%', color: Grey2 }}
                            align='center'
                        >
                            {!id ? "Location successfully created." : "Location successfully updated."}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center' sx={{ marginBottom: '4%' }}>
                        <PrimaryButton
                            width={70}
                            useLargeButton
                            buttonText='View Location Details'
                            onClick={() => navigate.push(`/Location/${data.id}`, {replace: true})}
                        />
                    </Grid>
                    <Grid item xs={12} align='center' sx={{ marginBottom: '4%' }}>
                        <SecondaryButton
                            useLargeButton
                            width={70}
                            onClick={() => window.location.reload()}
                            buttonText='Create New Location'
                        />
                    </Grid>
                </Grid>
            </CommonModal>

            <ExitConfirmationModal
                open={exitModal}
                close={closeExitModal}
                stayOnClick={closeExitModal}
                leaveOnClick={() => history.goBack()}
            />
        </>
    );

};

export default withRouter(AddLocation);
